import { createAsyncThunk } from '@reduxjs/toolkit';
import { requestCartItemsChange } from '../../../services/api/methods/cart';
import { CART_REQUEST_DELAY } from '../../../config';
import type { CartItemUpdateProps } from '../../../types/cart';
import type { TRootState } from '../../rootReducer';
import { requestLoadingSetter } from '../helpers/requestLoadingSetter';
import { getCartItemData } from '../selectors';
import type {
  CartCollectedResponse,
  ProductItemId,
  SetDelayProps,
  UpdateDelayMap,
  UpdateDelayItem
} from '../types';
import { cartStoreName } from '../consts';
import { cartActions } from '../slice';
import { getNotEnoughInStockError } from '../errors';
import { cartRequestHandler } from './cartRequestHandler';
import {
  DelayInfo,
  getDelayInfo
} from '../helpers/getDelayInfo';

let updateDelayTimeout: NodeJS.Timeout = null;
let updateDelayMap: UpdateDelayMap = {};

const prepareUpdateData = () =>
  Object.entries(updateDelayMap).map(
    ([id, { delay, quantity }]) => ({
      id,
      delay,
      newQuantity: quantity
    })
  );

const setItemProps = (
  props: Partial<UpdateDelayItem> & {
    id: ProductItemId;
    traceId?: string;
  },
  state: TRootState,
  delayInfo: DelayInfo
) => {
  const { id, delay, quantity, traceId } = props;
  const cartItem = getCartItemData(id)(state);
  const itemDelayInfo = delayInfo.find(
    (delayItem) => delayItem.id === id
  );

  if (id in updateDelayMap) {
    const mapItem = updateDelayMap[id];

    if (typeof delay === 'boolean') {
      mapItem.delay = delay;
      mapItem.oldDelay = itemDelayInfo.delay;
    } else if (!('delay' in mapItem)) {
      mapItem.delay = itemDelayInfo.delay;
      mapItem.oldDelay = itemDelayInfo.delay;
    }

    if (typeof quantity === 'number') {
      mapItem.quantity = quantity;
      mapItem.oldQuantity = cartItem.quantity;
    } else if (!('quantity' in mapItem)) {
      mapItem.quantity = cartItem.quantity;
      mapItem.oldQuantity = cartItem.quantity;
    }

    if (traceId) {
      mapItem.tracesIdsList.push(traceId);
    }
  } else {
    updateDelayMap[id] = {
      delay: 'delay' in props ? delay : itemDelayInfo.delay,
      oldDelay: itemDelayInfo.delay,
      quantity:
        'quantity' in props ? quantity : cartItem.quantity,
      oldQuantity: cartItem.quantity,
      tracesIdsList: traceId ? [traceId] : []
    };
  }
};

export const updateItem = createAsyncThunk(
  cartStoreName + '/updateItemQuantity',
  async (
    payload:
      | CartItemUpdateProps
      | SetDelayProps
      | SetDelayProps[],
    thunkApi
  ) => {
    const ids =
      'id' in payload
        ? [payload.id]
        : payload.map(({ id }) => id);

    const delayInfo = await getDelayInfo();
    const timestamp = await requestLoadingSetter(
      ids,
      thunkApi
    );

    const state = thunkApi.getState() as TRootState;

    if (updateDelayTimeout) {
      clearTimeout(updateDelayTimeout);
    }

    if (Array.isArray(payload)) {
      payload.forEach(({ id, delay }) => {
        setItemProps({ id, delay }, state, delayInfo);
      });
    } else if ('quantity' in payload) {
      setItemProps(
        {
          id: payload.id,
          quantity: payload.quantity,
          traceId: payload.traceId
        },
        state,
        delayInfo
      );
    } else {
      setItemProps(
        {
          id: payload.id,
          delay: payload.delay
        },
        state,
        delayInfo
      );
    }

    const request = async () => {
      const result = await requestCartItemsChange(
        prepareUpdateData()
      );

      thunkApi.dispatch(
        cartRequestHandler({
          id: Object.keys(updateDelayMap),
          result,
          timestamp,
          traceId:
            'traceId' in payload
              ? payload.traceId
              : undefined,
          revertCallback: () => {
            thunkApi.dispatch(
              cartActions.revertCartItems(updateDelayMap)
            );

            updateDelayMap = {};
          },
          successCallback: () => {
            updateDelayMap = {};

            if ('quantity' in payload) {
              const newCartItem = (
                result as CartCollectedResponse
              ).products.find(({ id }) => id == payload.id);

              if (
                'quantity' in payload &&
                payload.quantity > newCartItem.quantity
              ) {
                thunkApi.dispatch(
                  cartActions.pushCartError(
                    getNotEnoughInStockError(
                      payload.id,
                      newCartItem.quantity
                    )
                  )
                );
              }
            }
          }
        })
      );
    };

    updateDelayTimeout = setTimeout(
      request,
      CART_REQUEST_DELAY
    );
  }
);
